/**
 * Streaming variant of the agent loop — for voice mode.
 *
 * Identical logic to `agentLoop.ts` but uses `sendMessageStream` on the
 * final text-reply turn so that text tokens arrive as they are generated.
 * The `onTextChunk` callback feeds the SentenceBuffer in `tts.ts`, allowing
 * TTS to start speaking the first sentence while Gemini is still generating
 * the rest — reducing perceived latency from ~2 s to ~500 ms.
 *
 * Tool-call turns are still awaited synchronously (they produce no streamed
 * text anyway); only the last text-generating turn streams.
 */

import type { ChatSession, EnhancedGenerateContentResponse, Part } from "firebase/ai";
import { mergeGroundingSources, extractGroundingSources } from "./grounding";
import { executeCall, type AgentCallbacks } from "./agentLoop";
import { MAX_TURNS } from "@amazing-wedding/ai-planner";
import type { AgentTurnResult, GroundingSource, ToolActivity, ToolContext } from "./types";

export interface StreamingAgentCallbacks extends AgentCallbacks {
  /**
   * Called with each streamed text chunk as it arrives from the model.
   * Only fires on the final text-generating turn (not during tool calls).
   * Feed these chunks directly into a `SentenceBuffer` for TTS.
   */
  onTextChunk?: (chunk: string) => void;
}

function collectSources(
  response: EnhancedGenerateContentResponse,
  sources: GroundingSource[],
): void {
  // EnhancedGenerateContentResponse satisfies the GenerateContentResult shape
  // that extractGroundingSources expects.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mergeGroundingSources(sources, extractGroundingSources({ response } as any));
}

/**
 * Streaming agent loop.
 *
 * Drop-in replacement for `runAgentTurn` in voice mode. The returned
 * `AgentTurnResult.text` contains the full response text (same as the
 * non-streaming version) so callers that already have text don't need
 * a separate accumulation step.
 */
export async function runAgentTurnStream(
  chat: ChatSession,
  userContent: string | Part[],
  ctx: ToolContext,
  callbacks: StreamingAgentCallbacks,
): Promise<AgentTurnResult> {
  const activities: ToolActivity[] = [];
  const sources: GroundingSource[] = [];
  const ctxWithSources: ToolContext = {
    ...ctx,
    appendSources: (incoming) => mergeGroundingSources(sources, incoming),
  };

  try {
    let currentContent: string | Part[] = userContent;

    for (let turn = 0; turn <= MAX_TURNS; turn++) {
      const streamResult = await chat.sendMessageStream(currentContent);

      // Iterate the live stream, forwarding text chunks to the caller.
      // During a tool-call turn Gemini emits no text — the loop body is a no-op.
      let streamedText = "";
      for await (const chunk of streamResult.stream) {
        const text = chunk.text();
        if (text) {
          streamedText += text;
          callbacks.onTextChunk?.(text);
        }
      }

      // Await the aggregated response to inspect for function calls.
      // This resolves immediately (stream is already consumed above).
      const response = await streamResult.response;
      collectSources(response, sources);

      const calls = response.functionCalls();
      if (!calls || calls.length === 0) {
        // No tool calls — text was already streamed, we are done.
        const finalText = streamedText || response.text();
        return { text: finalText, activities, sources };
      }

      if (turn === MAX_TURNS) {
        // Reached the cap — return whatever text we have
        const fallback =
          response.text() ||
          "Nie udało się dokończyć tej operacji w kilku krokach. Spróbuj sformułować prośbę inaczej.";
        return { text: fallback, activities, sources };
      }

      // Execute all requested tool calls, then loop back with the results.
      const responseParts: Part[] = [];
      for (const call of calls) {
        responseParts.push(await executeCall(call, ctxWithSources, callbacks, activities));
      }
      currentContent = responseParts;
    }

    // Should not be reachable, but TypeScript needs it.
    return {
      text: "Nie udało się dokończyć tej operacji.",
      activities,
      sources,
    };
  } finally {
    callbacks.onProgress?.(null);
  }
}
